import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Form } from 'react-final-form';
import validations from 'validations';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import RichTextEditorField, {
  RichTextEditorFieldProps,
} from 'components/RichTextEditorField/RichTextEditorField.component';

import './RichTextEditorExpandModal.styles.scss';
import './RichTextEditorExpandModal.styles.responsive.scss';

const RichTextEditorExpandModal: React.ForwardRefRenderFunction<
  ModalRef,
  RichTextEditorFieldProps
> = (props, ref) => {
  const {
    className,
    initialValue,
    label,
    expandedModalLabel,
    name,
    required,
    onChange,
    ...rest
  } = props;

  const classes = classNames('rich-text-editor-expand-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const formInitialValue = useMemo(
    () => ({ [name]: initialValue }),
    [name, initialValue],
  );

  const onSubmit = useCallback(
    (values) => {
      onChange(values[name]);
      modal?.close();
    },
    [name, modal, onChange],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t(expandedModalLabel) || label}
    >
      <Form initialValues={formInitialValue} onSubmit={onSubmit}>
        {({ submitting, invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <RichTextEditorField
              {...rest}
              name={name}
              canExpand={false}
              required={required}
              {...(required && { validate: validations.richEditorRequired() })}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('save')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(RichTextEditorExpandModal);
